import { Add, DeleteOutline, Home } from "@mui/icons-material";
import { Box, Breadcrumbs, Button, IconButton, MenuItem, Stack, TextField, Tooltip, Typography } from "@mui/material";
import { COLORS, primaryContainedButtonSx } from "assets";
import { FormSection, SelectInput, StatusPill, TextInput } from "components";
import { BREAK_GLASS_APPROVED_EVENT, BREAK_GLASS_EVENT, MSG_CODE } from "config/constants";
import { useContextController } from "context";
import { IncidentApiError, incidentsApi } from "incidents/api";
import { filingCard } from "incidents/bc";
import { buildCompliance } from "incidents/compliance";
import {
  displayStatus,
  documentationUnlocked,
  elapsedSinceAlert,
  isAwaitingStaffReview,
  isRecordLocked,
  displayCopy,
  statusLabel,
  typeLabel,
  typeSubtype
} from "incidents/display";
import { incidentAccess } from "incidents/roles";
import { formatWhen } from "incidents/time";
import {
  AmendmentChange,
  FalseAlertReason,
  Incident,
  IncidentDocumentation,
  IncidentNotification,
  ReasonableGroundsDecision,
  YesNoUnsure
} from "incidents/types";
import { buildFieldErrors, FINALIZE_TOAST, FINALIZE_TOAST_FR } from "incidents/fieldErrors";
import { documentationSections } from "incidents/sections";
import { validateFinalize } from "incidents/validation";
import PrivateLayout from "layouts/PrivateLayout";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { fieldSx, primaryButtonSx, SectionTitle, SourceTag, useActor, usePhrase } from "./common";
import { DocumentationForm } from "./documentationForm";

const REASONS: { value: FalseAlertReason; en: string; fr: string }[] = [
  { value: "no_one_present", en: "No one present", fr: "Personne présente" },
  { value: "normal_activity", en: "Normal activity", fr: "Activité normale" },
  { value: "visitor_or_staff", en: "Visitor or staff", fr: "Visiteur ou membre du personnel" },
  { value: "camera_or_technical", en: "Camera or technical issue", fr: "Problème de caméra ou technique" },
  { value: "other", en: "Other", fr: "Autre" }
];

export default function IncidentDetail({ preview = false }: { preview?: boolean }) {
  const { id = "" } = useParams();
  const [params] = useSearchParams();
  const previewRole = params.get("as") === "supervisor" ? "supervisor" : preview ? "nurse" : undefined;
  const phrase = usePhrase();
  const locale = phrase("en", "fr") === "fr" ? "fr" : "en";
  const { enqueueSnackbar } = useSnackbar();
  const [{ userData }] = useContextController();
  const actor = useActor(preview ? previewRole : undefined);
  const [incident, setIncident] = useState<Incident | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [messages, setMessages] = useState<string[]>([]);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState<IncidentDocumentation>({});
  const [notes, setNotes] = useState<IncidentNotification[]>([]);
  const [decision, setDecision] = useState<ReasonableGroundsDecision>({ decision: null });
  const [reason, setReason] = useState<FalseAlertReason>("normal_activity");
  const [falseNote, setFalseNote] = useState("");
  const [risk, setRisk] = useState<"yes" | "no">("no");
  const [concern, setConcern] = useState<YesNoUnsure>("no");
  const [amendReason, setAmendReason] = useState("");
  const [amendRows, setAmendRows] = useState([{ field: "Location", customField: "", old: "", new: "" }]);
  const [reviewNote, setReviewNote] = useState("");
  const [chain, setChain] = useState<string | null>(null);
  const [provinceConfirmed, setProvinceConfirmed] = useState(false);

  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const onApproved = () => setReloadKey((k) => k + 1);
    window.addEventListener(BREAK_GLASS_APPROVED_EVENT, onApproved);
    return () => window.removeEventListener(BREAK_GLASS_APPROVED_EVENT, onApproved);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    incidentsApi
      .get(id)
      .then((record) => {
        if (cancelled) return;
        setIncident(record);
        setError(null);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        if (err instanceof IncidentApiError && err.msgCode === MSG_CODE.BREAK_GLASS_REQUIRED) {
          window.dispatchEvent(new CustomEvent(BREAK_GLASS_EVENT));
        }
        setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, reloadKey]);

  useEffect(() => {
    if (!incident) return;
    setDraft(incident.documentation ?? {});
    setNotes(incident.notifications ?? []);
    setDecision(incident.reasonable_grounds_decision ?? { decision: null });
    setProvinceConfirmed(Boolean(incident.province_confirmed));

  }, [incident]);

  const access = incidentAccess(userData, incident?.facility_id, preview ? previewRole : undefined);
  const base = preview ? "/dev/incidents" : "/incidents";
  const locked = incident ? isRecordLocked(incident) : false;
  const unlocked = incident ? documentationUnlocked(incident) : false;
  const waiting = incident ? isAwaitingStaffReview(incident) : false;
  const compliance = incident ? buildCompliance(incident, new Date()) : null;

  const amendFieldOptions = [
    { value: "Location", label: phrase("Location", "Emplacement") },
    { value: "Event time", label: phrase("Event time", "Heure de l'événement") },
    { value: "Discovered at", label: phrase("Discovered at", "Découvert à") },
    { value: "Observed", label: phrase("Observed", "Observé") },
    { value: "Care given", label: phrase("Care given", "Soins prodigués") },
    { value: "Outcome", label: phrase("Outcome", "Issue") },
    { value: "_other", label: phrase("Other field…", "Autre champ…") }
  ];

  function updateAmendRow(index: number, patch: Partial<(typeof amendRows)[0]>) {
    setAmendRows((rows) => rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  }

  function amendChangesForSubmit(): AmendmentChange[] {
    return amendRows
      .map((row) => ({
        field: row.field === "_other" ? row.customField.trim() : row.field,
        old: row.old,
        new: row.new
      }))
      .filter((change) => change.field && (change.old.trim() || change.new.trim()));
  }

  async function submitAmendment() {
    if (!incident) return;
    const changes = amendChangesForSubmit();
    if (!amendReason.trim() || !changes.length) {
      const message = phrase(
        "Add a reason and at least one field change.",
        "Ajoutez une raison et au moins une modification."
      );
      enqueueSnackbar(message, { variant: "error" });
      return;
    }
    await run(
      () =>
        incidentsApi.amend(incident.id, {
          reason: amendReason.trim(),
          changes,
          actor
        }),
      phrase("Amendment added to the record.", "Modification ajoutée au dossier.")
    );
    setAmendReason("");
    setAmendRows([{ field: "Location", customField: "", old: "", new: "" }]);
  }

  async function checkRecordChain() {
    if (!incident) return;
    try {
      const result = await incidentsApi.verify(incident.id);
      const message = result.valid
        ? phrase("Record chain checks out.", "La chaîne du dossier est intacte.")
        : phrase("Record chain does not check out.", "La chaîne du dossier ne concorde pas.");
      setChain(message);
      enqueueSnackbar(message, { variant: result.valid ? "success" : "warning" });
    } catch (err) {
      const message = err instanceof Error ? err.message : phrase("Could not verify the record chain.", "Impossible de vérifier la chaîne du dossier.");
      enqueueSnackbar(message, { variant: "error" });
    }
  }

  function applyValidationMessages(problems: string[]) {
    if (!incident) {
      setFieldErrors(buildFieldErrors(problems));
      return;
    }
    const missingFields = documentationSections({
      type: incident.type,
      province: incident.province,
      documentation: draft,
      notifications: notes,
      reasonable_grounds_decision: decision,
      confirmed_at: incident.confirmed_at,
      alert_at: incident.alert_at,
      bc_template: incident.bc_template,
      site_jurisdiction: incident.site_jurisdiction,
      province_confirmed: provinceConfirmed,
      room: incident.room,
      location: incident.location,
      actor_user_id: actor.user_id,
      locked_at: incident.locked_at
    }).flatMap((section) => section.missing);
    setFieldErrors(buildFieldErrors(problems, missingFields));
    enqueueSnackbar(phrase(FINALIZE_TOAST, FINALIZE_TOAST_FR), { variant: "error" });
  }

  async function run(task: () => Promise<Incident | { incident: Incident; message?: string }>, ok?: string) {
    setBusy(true);
    setMessages([]);
    setError(null);
    try {
      const result = await task();
      const next = "incident" in result ? result.incident : result;
      setIncident(next);
      setFieldErrors({});
      if ("message" in result && result.message) {
        setMessages([result.message]);
        enqueueSnackbar(result.message, { variant: "success" });
      } else if (ok) {
        setMessages([ok]);
        enqueueSnackbar(ok, { variant: "success" });
      }
    } catch (err) {
      if (err instanceof IncidentApiError && err.errors?.length) applyValidationMessages(err.errors);
      else {
        const message = err instanceof Error ? err.message : "Something went wrong.";
        setError(message);
        enqueueSnackbar(message, { variant: "error" });
      }
    } finally {
      setBusy(false);
    }
  }

  function documentationForSave(): IncidentDocumentation {
    if (!incident || incident.province !== "BC") return draft;
    return {
      ...draft,
      resident_name: incident.site_jurisdiction?.residentNamesAllowed === false ? "" : draft.resident_name,
      attestation_name: actor.name,
      attestation_user_id: actor.user_id
    };
  }

  async function saveDraft() {
    if (!incident) return;
    const documentation = documentationForSave();
    await run(
      () =>
        incidentsApi.saveDocumentation(incident.id, {
          documentation,
          notifications: notes,
          reasonable_grounds_decision: decision,
          actor,
          province: incident.province,
          province_confirmed: provinceConfirmed,
          bc_template: incident.bc_template
        }),
      phrase("Draft saved in NeuraVue. Nothing was sent.", "Brouillon enregistré dans NeuraVue. Rien n'a été envoyé.")
    );
  }

  async function finalize() {
    if (!incident) return;
    const documentation = documentationForSave();
    const previewRecord: Incident = {
      ...incident,
      documentation,
      notifications: notes,
      reasonable_grounds_decision: decision,
      province_confirmed: provinceConfirmed
    };
    const problems = validateFinalize(previewRecord, new Date(), actor);
    if (problems.length) {
      setMessages([]);
      applyValidationMessages(problems);
      return;
    }
    setBusy(true);
    setFieldErrors({});
    try {
      const saved = await incidentsApi.saveDocumentation(incident.id, {
        documentation,
        notifications: notes,
        reasonable_grounds_decision: decision,
        actor,
        province: incident.province,
        province_confirmed: provinceConfirmed,
        bc_template: incident.bc_template
      });
      const lockedRecord = await incidentsApi.submit(saved.id, {
        ...actor,
        name: documentation.attestation_name || actor.name,
        designation: documentation.attestation_designation || actor.designation
      });
      setIncident(lockedRecord);
      setFieldErrors({});
      setMessages([
        phrase(
          "Record finalized and locked in NeuraVue. It was not sent to a regulator.",
          "Dossier finalisé et verrouillé dans NeuraVue. Il n'a pas été envoyé à un organisme."
        )
      ]);
      enqueueSnackbar(
        phrase(
          "Record finalized and locked in NeuraVue. It was not sent to a regulator.",
          "Dossier finalisé et verrouillé dans NeuraVue. Il n'a pas été envoyé à un organisme."
        ),
        { variant: "success" }
      );
    } catch (err) {
      if (err instanceof IncidentApiError && err.errors?.length) applyValidationMessages(err.errors);
      else {
        const message = err instanceof Error ? err.message : "Could not finalize.";
        setError(message);
        enqueueSnackbar(message, { variant: "error" });
      }
    } finally {
      setBusy(false);
    }
  }

  const body = (
    <Box>
      <Breadcrumbs aria-label={phrase("Breadcrumb", "Fil d'Ariane")} sx={{ mb: 2 }}>
        <Tooltip title={phrase("Home", "Accueil")}>
          <IconButton component={Link} to="/" size="small" aria-label={phrase("Home", "Accueil")}>
            <Home fontSize="small" />
          </IconButton>
        </Tooltip>
        <Button component={Link} to={base} sx={{ textTransform: "none" }}>
          {phrase("Incident documentation", "Documentation des incidents")}
        </Button>
        <Typography color="text.primary">{id}</Typography>
      </Breadcrumbs>
      {loading ? <Typography>{phrase("Loading record…", "Chargement du dossier…")}</Typography> : null}
      {error ? (
        <Typography role="alert" color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      ) : null}
      {messages.length ? (
        <Box component="ul" role="status" sx={{ bgcolor: "#F8F1D8", p: 2, borderRadius: 2 }}>
          {messages.map((message) => (
            <li key={message}>{message}</li>
          ))}
        </Box>
      ) : null}
      {incident && compliance ? (
        <Box display="grid" gap={2} mt={2}>
          <Box>
            <Typography component="h1" sx={{ fontSize: 28, fontWeight: 700 }}>
              {incident.id}
            </Typography>
            <Typography sx={{ color: COLORS.surface.secondaryText, mt: 0.5 }}>
              {incident.facility_name} · {typeLabel(incident.type, locale)}
              {typeSubtype(incident.type, locale) ? ` · ${typeSubtype(incident.type, locale)}` : ""} ·{" "}
              {phrase("Room", "Chambre")} {incident.room || "—"}
            </Typography>
            <Box display="flex" gap={1} mt={1} flexWrap="wrap" alignItems="center">
              <StatusPill
                label={statusLabel(displayStatus(incident), locale)}
                tone={waiting ? "info" : locked ? "success" : "warning"}
              />
              {locked ? <StatusPill label={phrase("Locked", "Verrouillé")} tone="neutral" /> : null}
              <Button
                component={Link}
                to={`${base}/${incident.id}/export?${new URLSearchParams({
                  ...(preview ? { as: previewRole || "nurse" } : {}),
                  ...(incident.province === "BC" ? { format: "bc" } : {})
                }).toString()}`}
                sx={primaryButtonSx}
              >
                {phrase("Export / print", "Exporter / imprimer")}
              </Button>
            </Box>
          </Box>

          {waiting ? (
            <Box sx={{ p: 2, bgcolor: "#E8F3FB", borderRadius: 2 }} role="note">
              <Typography sx={{ fontWeight: 700 }}>
                {phrase("Awaiting staff review", "En attente de révision du personnel")}
              </Typography>
              <Typography>
                {phrase(
                  "This is the alert record and the timeline only. The incident form starts after you confirm that you opened the alert. Countdowns start at that confirmation, not at the alert time. Nothing is submitted for you.",
                  "Ceci est seulement le dossier d'alerte et la chronologie. Le formulaire commence quand vous confirmez avoir ouvert l'alerte. Les délais partent de cette confirmation, pas de l'heure de l'alerte. Rien n'est envoyé à votre place."
                )}
              </Typography>
            </Box>
          ) : null}

          <section>
            <SectionTitle
              title={phrase("Alert record", "Dossier d'alerte")}
              hint={phrase(
                "These facts come from the alert. They stay as first written.",
                "Ces faits viennent de l'alerte. Ils restent tels qu'écrits."
              )}
            />
            <Box sx={{ p: 2, bgcolor: "#F4F5F7", borderRadius: 2, display: "grid", gap: 1 }}>
              <SourceTag filledBySystem />
              <Fact label={phrase("Alert", "Alerte")} value={incident.alert_id} />
              <Fact label={phrase("Sub-type", "Sous-type")} value={incident.alert_subtype || "—"} />
              <Fact
                label={phrase("Alert time", "Heure de l'alerte")}
                value={formatWhen(incident.alert_at, locale === "fr" ? "fr-CA" : "en-CA")}
              />
              <Fact
                label={phrase("Camera and place", "Caméra et lieu")}
                value={`${incident.camera_id || "—"} · ${incident.location || "—"}`}
              />
              <Fact
                label={phrase("What the system recorded", "Ce que le système a enregistré")}
                value={displayCopy(incident.alert_summary)}
              />
              <Fact
                label={phrase("Staff notified", "Personnel avisé")}
                value={
                  incident.notified_staff
                    .map(
                      (person) =>
                        `${person.name} (${person.role}) ${formatWhen(person.at, locale === "fr" ? "fr-CA" : "en-CA")}`
                    )
                    .join("; ") || "—"
                }
              />
              <Fact
                label={phrase("Time since the alert", "Temps depuis l'alerte")}
                value={elapsedSinceAlert(incident, new Date(), locale).replace(/^.*:\s*/, "")}
              />
              <Typography sx={{ fontSize: 13, color: COLORS.surface.secondaryText }}>
                {phrase(
                  "No injury photos are stored here. A clip reference is an identifier only.",
                  "Aucune photo de blessure n'est conservée ici. La référence du clip est seulement un identifiant."
                )}{" "}
                {incident.clip_ref ? incident.clip_ref.id : ""}
              </Typography>
            </Box>
          </section>

          <section>
            <SectionTitle title={phrase("Response timeline", "Chronologie")} />
            <Box component="ol" sx={{ m: 0, pl: 3 }}>
              {incident.events.map((event) => (
                <li key={event.id}>
                  <Typography>
                    {formatWhen(event.at, locale === "fr" ? "fr-CA" : "en-CA")} —{" "}
                    {event.actor_name || event.actor_user_id} — {event.type.replace(/_/g, " ")}
                    {event.payload?.note ? ` — ${event.payload.note}` : ""}
                  </Typography>
                </li>
              ))}
            </Box>
          </section>

          {waiting && access.canDocument ? (
            <Box display="flex" gap={1} flexWrap="wrap">
              <Button
                variant="contained"
                disabled={busy}
                sx={{ ...primaryButtonSx, ...primaryContainedButtonSx }}
                onClick={() => run(() => incidentsApi.confirm(incident.id, actor))}
              >
                {phrase(
                  "I opened this alert — start documentation",
                  "J'ai ouvert cette alerte — commencer la documentation"
                )}
              </Button>
              <Button
                disabled={busy}
                sx={primaryButtonSx}
                onClick={() =>
                  run(() =>
                    incidentsApi.escalate(
                      incident.id,
                      actor,
                      phrase("Escalated from review.", "Escaladé pendant la révision.")
                    )
                  )
                }
              >
                {phrase("Escalate", "Escalader")}
              </Button>
            </Box>
          ) : null}

          {incident.status === "false_alert_pending_confirm" && incident.false_alert ? (
            <section>
              <SectionTitle
                title={phrase("Dismissal pending confirmation", "Rejet en attente de confirmation")}
                hint={phrase(
                  "Marking an alert false does not remove anyone's duty to report. A supervisor cannot block escalation.",
                  "Marquer une alerte comme fausse n'enlève pas le devoir de signaler. Un superviseur ne peut pas bloquer l'escalade."
                )}
              />
              <Fact label={phrase("Reason", "Raison")} value={incident.false_alert.reason_code.replace(/_/g, " ")} />
              <Fact label={phrase("Note", "Note")} value={incident.false_alert.note || "—"} />
              <Fact
                label={phrase("Marked by", "Marqué par")}
                value={`${incident.false_alert.dismissed_by_name} · ${formatWhen(incident.false_alert.dismissed_at)}`}
              />
              {access.canSupervise ? (
                <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                  <Button
                    variant="contained"
                    disabled={busy || actor.user_id === incident.false_alert.dismissed_by}
                    sx={{ ...primaryButtonSx, ...primaryContainedButtonSx }}
                    onClick={() =>
                      run(() => incidentsApi.confirmFalseAlert(incident.id, { decision: "confirm", actor }))
                    }
                  >
                    {phrase("Confirm dismissal", "Confirmer le rejet")}
                  </Button>
                  <Button
                    disabled={busy || actor.user_id === incident.false_alert.dismissed_by}
                    sx={primaryButtonSx}
                    onClick={() =>
                      run(() =>
                        incidentsApi.confirmFalseAlert(incident.id, { decision: "reject", note: reviewNote, actor })
                      )
                    }
                  >
                    {phrase("Reject — open the incident", "Rejeter — ouvrir l'incident")}
                  </Button>
                </Box>
              ) : (
                <Typography>
                  {phrase(
                    "A manager or director of care must confirm this dismissal. You can still escalate.",
                    "Une gestionnaire ou la directrice des soins doit confirmer ce rejet. Vous pouvez encore escalader."
                  )}
                </Typography>
              )}
              {access.canDocument ? (
                <Button
                  sx={{ ...primaryButtonSx, mt: 1 }}
                  disabled={busy}
                  onClick={() => run(() => incidentsApi.escalate(incident.id, actor, "Escalated."))}
                >
                  {phrase("Escalate", "Escalader")}
                </Button>
              ) : null}
            </section>
          ) : null}

          {(waiting || (unlocked && !locked)) &&
          access.canDocument &&
          incident.status !== "false_alert_pending_confirm" ? (
            <section>
              <SectionTitle
                title={phrase("False alert", "Fausse alerte")}
                hint={phrase(
                  "Marking an alert false does not remove anyone's duty to report under provincial law.",
                  "Marquer une alerte comme fausse n'enlève pas le devoir de signaler prévu par la loi."
                )}
              />
              <Box display="grid" gap={1.5} maxWidth={640}>
                <TextField
                  select
                  label={phrase("Reason", "Raison")}
                  value={reason}
                  onChange={(event) => setReason(event.target.value as FalseAlertReason)}
                  sx={fieldSx}
                >
                  {REASONS.map((item) => (
                    <MenuItem key={item.value} value={item.value}>
                      {locale === "fr" ? item.fr : item.en}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  label={phrase("Note", "Note")}
                  value={falseNote}
                  onChange={(event) => setFalseNote(event.target.value)}
                  multiline
                  minRows={2}
                  sx={fieldSx}
                />
                <TextField
                  select
                  label={phrase("Any sign of injury or distress?", "Signe de blessure ou de détresse?")}
                  value={risk}
                  onChange={(event) => setRisk(event.target.value as "yes" | "no")}
                  sx={fieldSx}
                >
                  <MenuItem value="no">{phrase("No", "Non")}</MenuItem>
                  <MenuItem value="yes">
                    {phrase("Yes — this stays an incident", "Oui — ceci reste un incident")}
                  </MenuItem>
                </TextField>
                <TextField
                  select
                  label={phrase("Any concern about care or conduct?", "Inquiétude sur les soins ou la conduite?")}
                  value={concern}
                  onChange={(event) => setConcern(event.target.value as YesNoUnsure)}
                  sx={fieldSx}
                >
                  <MenuItem value="no">{phrase("No", "Non")}</MenuItem>
                  <MenuItem value="unsure">{phrase("Unsure — cannot dismiss", "Incertain — ne pas rejeter")}</MenuItem>
                  <MenuItem value="yes">{phrase("Yes — cannot dismiss", "Oui — ne pas rejeter")}</MenuItem>
                </TextField>
                <Button
                  sx={primaryButtonSx}
                  disabled={busy}
                  onClick={() =>
                    run(() =>
                      incidentsApi.falseAlert(incident.id, {
                        reason_code: reason,
                        note: falseNote,
                        risk_situation: risk,
                        concern,
                        actor
                      })
                    )
                  }
                >
                  {phrase("Mark false alert", "Marquer comme fausse alerte")}
                </Button>
              </Box>
            </section>
          ) : null}

          {incident.province === "BC" && unlocked ? <WhereToFile incident={incident} /> : null}

          {unlocked && compliance.clocks.length ? (
            <section aria-label={phrase("Compliance hints", "Repères de conformité")}>
              <SectionTitle
                title={phrase("Compliance hints", "Repères de conformité")}
                hint={compliance.confirmation_note}
              />
              <Typography sx={{ mb: 1 }}>
                {phrase("Confirmed at", "Confirmé à")}{" "}
                {formatWhen(incident.confirmed_at, locale === "fr" ? "fr-CA" : "en-CA")} ·{" "}
                {elapsedSinceAlert(incident, new Date(), locale)}
              </Typography>
              {compliance.route_hint ? <Typography sx={{ mb: 1 }}>{compliance.route_hint}</Typography> : null}
              {compliance.next_business_morning ? (
                <Typography sx={{ mb: 1 }}>
                  {phrase("Next business morning", "Prochain matin ouvrable")}: {compliance.next_business_morning}
                </Typography>
              ) : null}
              {compliance.province_note ? <Typography>{compliance.province_note}</Typography> : null}
              <Box component="ul" sx={{ pl: 2 }}>
                {compliance.clocks.map((clock) => (
                  <li key={clock.id}>
                    <Typography>
                      <strong>{clock.label}</strong> —{" "}
                      {clock.state === "done"
                        ? phrase("Done", "Fait")
                        : clock.state === "advisory"
                          ? phrase("Optional", "Facultatif")
                          : clock.state === "due_now"
                            ? phrase("Due now", "À faire maintenant")
                            : clock.state === "overdue"
                              ? clock.site_policy
                                ? phrase("Overdue (site policy)", "En retard (politique du site)")
                                : phrase("Overdue", "En retard")
                              : clock.state === "needed"
                                ? phrase("Needed to finalize", "Requis avant de finaliser")
                                : clock.due_at
                                  ? `${phrase("Due", "Échéance")} ${formatWhen(clock.due_at, locale === "fr" ? "fr-CA" : "en-CA")}`
                                  : clock.state}
                    </Typography>
                    <Typography sx={{ color: COLORS.surface.secondaryText }}>{clock.detail}</Typography>
                  </li>
                ))}
              </Box>
            </section>
          ) : null}

          {unlocked ? (
            <DocumentationForm
              incident={incident}
              draft={draft}
              setDraft={setDraft}
              notes={notes}
              setNotes={setNotes}
              decision={decision}
              setDecision={setDecision}
              locked={locked}
              canDocument={access.canDocument}
              busy={busy}
              actorUserId={actor.user_id}
              actorName={actor.name}
              provinceConfirmed={provinceConfirmed}
              onProvinceConfirmed={setProvinceConfirmed}
              onSave={saveDraft}
              onFinalize={finalize}
              fieldErrors={fieldErrors}
            />
          ) : null}

          <FormSection
            title={phrase("Amendments", "Modifications")}
            description={phrase(
              "The original text stays visible. Add a reason and each change after the record is locked.",
              "Le texte original reste visible. Ajoutez une raison et chaque modification après verrouillage du dossier."
            )}
          >
            {incident.amendments.length ? (
              <Stack spacing={1.5} mb={locked && access.canDocument ? 2 : 0}>
                {incident.amendments.map((amendment) => (
                  <Box
                    key={amendment.id}
                    sx={{
                      p: 2,
                      borderRadius: `${COLORS.radius.md}px`,
                      border: `1px solid ${COLORS.surface.border}`,
                      bgcolor: "#F4F5F7"
                    }}
                  >
                    <Typography sx={{ fontWeight: 700 }}>
                      {amendment.author_name} · {formatWhen(amendment.at, locale === "fr" ? "fr-CA" : "en-CA")}
                    </Typography>
                    <Typography sx={{ mt: 0.5 }}>{amendment.reason}</Typography>
                    {amendment.changes.map((change) => (
                      <Typography key={`${amendment.id}-${change.field}`} sx={{ mt: 0.5, color: COLORS.surface.secondaryText }}>
                        <strong>{change.field}</strong> · {phrase("Original", "Original")} “{change.old}” ·{" "}
                        {phrase("Amended", "Modifié")} “{change.new}”
                      </Typography>
                    ))}
                  </Box>
                ))}
              </Stack>
            ) : (
              <Typography sx={{ color: COLORS.surface.secondaryText, mb: locked && access.canDocument ? 2 : 0 }}>
                {phrase("No amendments yet.", "Aucune modification.")}
              </Typography>
            )}
            {locked && access.canDocument ? (
              <Stack spacing={2} maxWidth={720}>
                <TextInput
                  label={phrase("Reason for this amendment", "Raison de cette modification")}
                  value={amendReason}
                  onChange={(event) => setAmendReason(event.target.value)}
                  fullWidth
                />
                {amendRows.map((row, index) => (
                  <Box
                    key={`amend-row-${index}`}
                    sx={{
                      p: 2,
                      borderRadius: `${COLORS.radius.md}px`,
                      border: `1px solid ${COLORS.surface.border}`
                    }}
                  >
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                      <Typography sx={{ fontWeight: 600 }}>
                        {phrase("Field change", "Modification de champ")} {amendRows.length > 1 ? index + 1 : ""}
                      </Typography>
                      {amendRows.length > 1 ? (
                        <IconButton
                          size="small"
                          aria-label={phrase("Remove change", "Retirer la modification")}
                          onClick={() => setAmendRows((rows) => rows.filter((_, i) => i !== index))}
                        >
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      ) : null}
                    </Box>
                    <Stack spacing={2}>
                      <SelectInput
                        label={phrase("Field", "Champ")}
                        value={row.field}
                        data={amendFieldOptions}
                        fullWidth
                        onChange={(event) => updateAmendRow(index, { field: String(event.target.value) })}
                      />
                      {row.field === "_other" ? (
                        <TextInput
                          label={phrase("Field name", "Nom du champ")}
                          value={row.customField}
                          onChange={(event) => updateAmendRow(index, { customField: event.target.value })}
                          fullWidth
                        />
                      ) : null}
                      <TextInput
                        label={phrase("Original text", "Texte original")}
                        value={row.old}
                        onChange={(event) => updateAmendRow(index, { old: event.target.value })}
                        fullWidth
                      />
                      <TextInput
                        label={phrase("Corrected text", "Texte corrigé")}
                        value={row.new}
                        onChange={(event) => updateAmendRow(index, { new: event.target.value })}
                        fullWidth
                      />
                    </Stack>
                  </Box>
                ))}
                <Box display="flex" gap={1} flexWrap="wrap">
                  <Button
                    startIcon={<Add />}
                    sx={primaryButtonSx}
                    onClick={() =>
                      setAmendRows((rows) => [...rows, { field: "Location", customField: "", old: "", new: "" }])
                    }
                  >
                    {phrase("Add another field change", "Ajouter un autre champ")}
                  </Button>
                  <Button
                    variant="contained"
                    disabled={busy}
                    sx={{ ...primaryButtonSx, ...primaryContainedButtonSx }}
                    onClick={() => void submitAmendment()}
                  >
                    {phrase("Save amendment", "Enregistrer la modification")}
                  </Button>
                </Box>
              </Stack>
            ) : locked ? (
              <Typography sx={{ color: COLORS.surface.secondaryText, mt: 1 }}>
                {phrase(
                  "Only staff who can document this record may add amendments.",
                  "Seul le personnel autorisé à documenter ce dossier peut ajouter des modifications."
                )}
              </Typography>
            ) : null}
          </FormSection>

          {incident.documentation?.supervisor_review ? (
            <FormSection
              title={phrase("Manager review", "Révision de la gestion")}
              description={phrase(
                "This is a separate entry, not a co-signature.",
                "Ceci est une entrée distincte, pas une contresignature."
              )}
            >
              <Box sx={{ p: 2, borderRadius: `${COLORS.radius.md}px`, bgcolor: "#F4F5F7" }}>
                <Typography sx={{ fontWeight: 700 }}>
                  {incident.documentation.supervisor_review.name},{" "}
                  {incident.documentation.supervisor_review.designation}
                </Typography>
                <Typography sx={{ color: COLORS.surface.secondaryText, mt: 0.5 }}>
                  {formatWhen(incident.documentation.supervisor_review.at, locale === "fr" ? "fr-CA" : "en-CA")}
                </Typography>
                <Typography sx={{ mt: 1 }}>{incident.documentation.supervisor_review.note || "—"}</Typography>
              </Box>
            </FormSection>
          ) : null}

          {locked && access.canSupervise && incident.status === "submitted_locked" && !incident.documentation?.supervisor_review ? (
            <FormSection
              title={phrase("Director of care review", "Révision de la directrice des soins")}
              description={phrase(
                "Your review is your own entry. It does not co-sign the nurse's attestation.",
                "Votre révision est votre propre entrée. Elle ne contresigne pas l'attestation de l'infirmière."
              )}
            >
              <Stack spacing={2} maxWidth={720}>
                <TextField
                  label={phrase("Review note", "Note de révision")}
                  value={reviewNote}
                  onChange={(event) => setReviewNote(event.target.value)}
                  multiline
                  minRows={3}
                  fullWidth
                  sx={fieldSx}
                />
                <Box>
                  <Button
                    variant="contained"
                    disabled={busy || !reviewNote.trim()}
                    sx={{ ...primaryButtonSx, ...primaryContainedButtonSx }}
                    onClick={() =>
                      run(
                        () =>
                          incidentsApi.review(incident.id, actor, reviewNote, actor.designation || "Director of care"),
                        phrase("Review recorded on this incident.", "Révision enregistrée sur cet incident.")
                      ).then(() => setReviewNote(""))
                    }
                  >
                    {phrase("Record my review", "Enregistrer ma révision")}
                  </Button>
                </Box>
              </Stack>
            </FormSection>
          ) : null}

          <FormSection
            title={phrase("Record integrity", "Intégrité du dossier")}
            description={phrase(
              "Verify the tamper-evident chain for this incident record.",
              "Vérifiez la chaîne inviolable de ce dossier d'incident."
            )}
          >
            <Button variant="outlined" sx={primaryButtonSx} onClick={() => void checkRecordChain()}>
              {phrase("Check record chain", "Vérifier la chaîne du dossier")}
            </Button>
            {chain ? (
              <Typography role="status" sx={{ mt: 1.5, color: COLORS.surface.secondaryText }}>
                {chain}
              </Typography>
            ) : null}
          </FormSection>
        </Box>
      ) : null}
    </Box>
  );

  if (preview) return <Box sx={{ p: 2, bgcolor: COLORS.surface.page, minHeight: "100vh" }}>{body}</Box>;
  return <PrivateLayout canAccess={() => true}>{body}</PrivateLayout>;
}

function WhereToFile({ incident }: { incident: Incident }) {
  const phrase = usePhrase();
  const card = filingCard(incident);
  return (
    <section>
      <SectionTitle
        title={phrase("Where to file", "Où déposer")}
        hint={phrase(
          "Copy this into the channel below. You send it. NeuraVue does not.",
          "Copiez ceci dans la voie ci-dessous. Vous l'envoyez. NeuraVue ne l'envoie pas."
        )}
      />
      <Box sx={{ p: 2, bgcolor: "#F4F5F7", borderRadius: 2, display: "grid", gap: 1 }}>
        {card.health_authority ? (
          <Typography>
            <strong>{card.health_authority.name}. </strong>
            {card.health_authority.channel}
          </Typography>
        ) : (
          <Typography>
            {phrase(
              "No health authority is on file. A facility admin sets it.",
              "Aucune autorité sanitaire n'est au dossier. Un administrateur l'inscrit."
            )}
          </Typography>
        )}
        {card.assisted_living_registry ? (
          <Typography>
            {card.assisted_living_registry.channel} {card.assisted_living_registry.form}.{" "}
            {card.assisted_living_registry.email}. {phrase("Fax", "Télécopieur")} {card.assisted_living_registry.fax}.
          </Typography>
        ) : null}
        <Typography sx={{ color: COLORS.surface.secondaryText }}>
          {phrase("Not verified against a current form:", "Non vérifié contre un formulaire actuel :")}{" "}
          {card.unverified.join("; ")}.
        </Typography>
      </Box>
    </section>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <Typography>
      <strong>{label}: </strong>
      {value}
    </Typography>
  );
}
